import { format } from "date-fns";
import type { ComponentProps } from "react";
import {
    type Control,
    Controller,
    type FieldPath,
    type FieldValues,
    type UseControllerProps,
} from "react-hook-form";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { DatePickerInput } from "../../date-time-picker/DateTimePicker";
import { FormField, FormInput, FormTextarea } from "../../form-field/FormField";
import type { FieldKind } from "./types";

type FieldOption = {
    id: string;
    label: string;
};

export function OwnedField<TFieldValues extends FieldValues>({
    id,
    label,
    kind,
    options,
    control,
    name,
    hint,
    placeholder,
    optional,
    required,
    rules,
    maxLength,
    inputMode,
    autoComplete,
    autoFocus,
    disabled,
    endMonth,
    onValueChange,
}: {
    id: string;
    label: string;
    kind: FieldKind;
    options?: readonly string[] | readonly FieldOption[];
    control: Control<TFieldValues>;
    name: FieldPath<TFieldValues>;
    hint?: string;
    placeholder?: string;
    optional?: boolean;
    required?: boolean;
    rules?: UseControllerProps<TFieldValues, FieldPath<TFieldValues>>["rules"];
    maxLength?: number;
    inputMode?: ComponentProps<"input">["inputMode"];
    autoComplete?: ComponentProps<"input">["autoComplete"];
    autoFocus?: boolean;
    disabled?: boolean;
    endMonth?: Date;
    /** Runs after a select changes, so that the fields depending on it can follow. */
    onValueChange?: (value: string) => void;
}) {
    return (
        <Controller
            control={control}
            name={name}
            rules={{
                ...rules,
                ...(required ? { required: `${label} is required` } : {}),
            }}
            render={({ field, fieldState }) => {
                const invalid = fieldState.invalid || undefined;

                return (
                    <FormField
                        label={label}
                        htmlFor={kind === "date" ? undefined : id}
                        hint={hint}
                        optional={optional}
                        required={required}
                        error={fieldState.error?.message}
                    >
                        {kind === "textarea" ? (
                            <FormTextarea
                                id={id}
                                name={field.name}
                                ref={field.ref}
                                value={String(field.value ?? "")}
                                placeholder={placeholder}
                                disabled={disabled}
                                aria-invalid={invalid}
                                onBlur={field.onBlur}
                                onChange={field.onChange}
                            />
                        ) : kind === "select" && options ? (
                            <SelectField
                                id={id}
                                options={options}
                                value={field.value ? String(field.value) : null}
                                placeholder={placeholder ?? label}
                                disabled={disabled}
                                invalid={fieldState.invalid}
                                onValueChange={(next) => {
                                    field.onChange(next);
                                    onValueChange?.(next);
                                }}
                            />
                        ) : kind === "date" ? (
                            <DatePickerInput
                                date={asDate(field.value)}
                                onDateChange={field.onChange}
                                invalid={fieldState.invalid}
                                endMonth={endMonth}
                            />
                        ) : (
                            <FormInput
                                id={id}
                                name={field.name}
                                ref={field.ref}
                                type={kind === "number" ? "number" : "text"}
                                value={String(field.value ?? "")}
                                placeholder={placeholder}
                                maxLength={maxLength}
                                inputMode={inputMode}
                                autoComplete={autoComplete}
                                autoFocus={autoFocus}
                                disabled={disabled}
                                aria-invalid={invalid}
                                onBlur={field.onBlur}
                                onChange={field.onChange}
                            />
                        )}
                    </FormField>
                );
            }}
        />
    );
}

export function ReadOnlyField({ label, value }: { label: string; value?: string }) {
    if (!value) return null;

    return (
        <FormField label={label}>
            <p className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-500 leading-4">
                {value}
            </p>
        </FormField>
    );
}

export function DateField({
    id,
    label,
    date,
    onDateChange,
    disabled,
    error,
    endMonth,
}: {
    id: string;
    label: string;
    date?: Date;
    onDateChange?: (date: Date | undefined) => void;
    disabled?: boolean;
    error?: string;
    endMonth?: Date;
}) {
    if (disabled) {
        return (
            <ReadOnlyField
                label={label}
                value={date ? format(date, "d MMM yyyy") : undefined}
            />
        );
    }

    return (
        <FormField label={label} htmlFor={id} error={error}>
            <DatePickerInput
                date={date}
                onDateChange={onDateChange}
                endMonth={endMonth}
            />
        </FormField>
    );
}

function SelectField({
    id,
    options,
    value,
    placeholder,
    disabled,
    invalid,
    onValueChange,
}: {
    id: string;
    options: readonly string[] | readonly FieldOption[];
    value: string | null;
    placeholder: string;
    disabled?: boolean;
    invalid?: boolean;
    onValueChange: (next: string) => void;
}) {
    const items = toFieldOptions(options);

    return (
        <Select
            items={Object.fromEntries(items.map((item) => [item.id, item.label]))}
            value={value}
            disabled={disabled}
            onValueChange={(next) => {
                if (typeof next === "string") onValueChange(next);
            }}
        >
            <SelectTrigger
                id={id}
                className="w-full"
                aria-invalid={invalid || undefined}
            >
                <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
                {items.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                        {item.label}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

function toFieldOptions(
    options: readonly string[] | readonly FieldOption[],
): FieldOption[] {
    return options.map((option) =>
        typeof option === "string" ? { id: option, label: option } : option,
    );
}

function asDate(value: unknown): Date | undefined {
    return value instanceof Date ? value : undefined;
}
